import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Eye, EyeOff, CheckCircle2, ArrowLeft } from "lucide-react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp"

enum SignupStep {
  REGISTRATION = 0,
  OTP_VERIFICATION = 1
}

const SignupPage: React.FC = () => {
  const [formData, setFormData] = useState({
    businessName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState<SignupStep>(SignupStep.REGISTRATION);
  const [otp, setOtp] = useState('');
  
  const { signup, verifyOtp, resendOtp } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (checked: boolean) => {
    setFormData(prev => ({ ...prev, acceptTerms: checked }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    
    if (!formData.businessName || !formData.email || !formData.phoneNumber || !formData.password) {
      setError('Bitte füllen Sie alle erforderlichen Felder aus');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Die Passwörter stimmen nicht überein');
      return;
    }
    
    if (!formData.acceptTerms) {
      setError('Bitte akzeptieren Sie die Nutzungsbedingungen');
      return;
    }

    try {
      setLoading(true);
      const success = await signup(
        formData.businessName,
        formData.email,
        formData.phoneNumber,
        formData.password
      );
      
      if (success) {
        setActiveStep(SignupStep.OTP_VERIFICATION);
      } else {
        setError('Konto konnte nicht erstellt werden');
      }
    } catch (err) {
      setError('Ein Fehler ist während der Registrierung aufgetreten');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    
    if (!otp || otp.length < 6) {
      setError('Bitte geben Sie den vollständigen Code ein');
      return;
    }

    try {
      setLoading(true);
      const success = await verifyOtp(formData.email, otp);
      
      if (success) {
        navigate('/login', { state: { registrationSuccess: true } });
      } else {
        setError('Ungültiger Code. Bitte versuchen Sie es erneut.');
      }
    } catch (err) {
      setError('Ein Fehler ist bei der Verifizierung aufgetreten');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      setLoading(true);
      await resendOtp(formData.email);
      setError(null);
    } catch (err) {
      setError('Fehler beim erneuten Senden des Codes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:grid lg:grid-cols-2">
      {/* Linke Seite: Branding (Desktop) */}
      <div className="hidden lg:flex flex-col justify-between bg-muted p-12 relative overflow-hidden">
        <div className="z-10">
          <Logo />
          <div className="mt-20 max-w-md">
            <h1 className="text-4xl font-bold tracking-tight text-foreground mb-6">
              Starten Sie mit WhatsApp Termin AI
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Erstellen Sie in wenigen Minuten Ihr Konto und automatisieren Sie Ihre Kundenkommunikation.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-muted-foreground">
                <CheckCircle2 className="h-5 w-5 text-success-strong" />
                <span>Kostenlose Testphase</span>
              </div>
              <div className="flex items-center gap-3 text-muted-foreground">
                <CheckCircle2 className="h-5 w-5 text-success-strong" />
                <span>Keine Kreditkarte erforderlich</span>
              </div>
              <div className="flex items-center gap-3 text-muted-foreground">
                <CheckCircle2 className="h-5 w-5 text-success-strong" />
                <span>Sofort einsatzbereit</span>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute inset-0 z-0 opacity-5 pointer-events-none">
           <img src="/images/timeglobe.svg" alt="Background" className="w-[150%] h-[150%] -translate-x-1/4 -translate-y-1/4 object-contain" />
        </div>

        <div className="z-10 text-xs text-muted-foreground/70">
          © {new Date().getFullYear()} EcomTask. Alle Rechte vorbehalten.
        </div>
      </div>

      {/* Rechte Seite: Signup Form */}
      <div className="flex-1 flex flex-col justify-center items-center p-6 bg-background relative">
        <div className="w-full max-w-md mx-auto space-y-8">
          <div className="flex flex-col space-y-2 text-center lg:text-left">
            <div className="lg:hidden flex justify-center mb-6">
              <Logo />
            </div>
            <h2 className="text-3xl font-bold tracking-tight">
              {activeStep === SignupStep.REGISTRATION ? 'Konto erstellen' : 'E-Mail bestätigen'}
            </h2>
            <p className="text-muted-foreground">
              {activeStep === SignupStep.REGISTRATION
                ? 'Geben Sie Ihre Daten ein, um loszulegen' 
                : `Wir haben einen Code an ${formData.email} gesendet`}
            </p>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {activeStep === SignupStep.REGISTRATION ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="businessName">Firmenname</Label>
                <Input
                  id="businessName"
                  name="businessName"
                  placeholder="Ihr Unternehmen GmbH"
                  required
                  value={formData.businessName}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail-Adresse</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="name@firma.de"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Telefonnummer</Label>
                  <Input
                    id="phoneNumber"
                    name="phoneNumber"
                    type="tel"
                    placeholder="+49 123 4567890"
                    required
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    disabled={loading}
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-10 w-10 text-muted-foreground/70 hover:text-muted-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    disabled={loading}
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-10 w-10 text-muted-foreground/70 hover:text-muted-foreground"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <div className="flex items-start space-x-2 pt-2">
                <Checkbox 
                  id="terms" 
                  checked={formData.acceptTerms}
                  onCheckedChange={handleCheckboxChange}
                  disabled={loading}
                />
                <Label
                  htmlFor="terms"
                  className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Ich stimme den <RouterLink to="/terms" className="text-primary-strong hover:underline">Nutzungsbedingungen</RouterLink> und der <RouterLink to="/privacy" className="text-primary-strong hover:underline">Datenschutzerklärung</RouterLink> zu
                </Label>
              </div>

              <Button 
                type="submit" 
                className="w-full h-11 text-base mt-2 bg-[#1967D2] hover:bg-[#1756B6]" 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Konto erstellen...
                  </>
                ) : (
                  'Registrieren'
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleOtpSubmit} className="space-y-6">
              <div className="flex justify-center py-4">
                <InputOTP
                  maxLength={6}
                  value={otp}
                  onChange={(value) => setOtp(value)}
                  disabled={loading}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                  </InputOTPGroup>
                  <div className="w-2" />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button 
                type="submit" 
                className="w-full h-11 text-base bg-[#1967D2] hover:bg-[#1756B6]" 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Bestätigen...
                  </>
                ) : (
                  'Bestätigen'
                )}
              </Button>

              <div className="text-center space-y-4">
                 <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={loading}
                    className="text-sm text-primary-strong hover:underline bg-transparent border-none cursor-pointer"
                  >
                    Keinen Code erhalten? Erneut senden
                  </button>
                  
                  <div className="block">
                    <button
                      type="button"
                      onClick={() => setActiveStep(SignupStep.REGISTRATION)}
                      className="text-sm text-muted-foreground hover:text-foreground flex items-center justify-center gap-1 mx-auto"
                    >
                      <ArrowLeft className="h-3 w-3" />
                      Zurück zur Registrierung
                    </button>
                  </div>
              </div>
            </form>
          )}

          {activeStep === SignupStep.REGISTRATION && (
            <div className="text-center text-sm">
              Bereits ein Konto?{' '}
              <RouterLink to="/login" className="font-medium text-primary-strong hover:underline">
                Jetzt anmelden
              </RouterLink>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="absolute bottom-4 left-0 right-0 flex justify-center items-center opacity-70">
          <span className="text-xs text-muted-foreground mr-2">powered by</span>
          <img src="/images/EcomTask_logo.svg" alt="EcomTask" className="h-6 opacity-60 grayscale hover:grayscale-0 transition-all" />
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
